import { useState, useEffect, useRef } from 'react';

import { useCookies } from 'react-cookie';
import { useParams, useLocation } from 'react-router-dom';

import { LoginType, LoginTypeInfo } from '@common/types';
import config from '@config';
import { useAccount } from '@store/account';

import { captureException } from './sentry';

export const useResetUserSession = () => {
  const cookieManager = useCookies(['csrftoken']);
  const cookies = cookieManager[0];
  const removeCookie = cookieManager[2];

  useEffect(() => {
    if (cookies.csrftoken) {
      removeCookie('csrftoken', { path: '/' });
    }
  }, []);
};

/**
 * Check if the browser supports ES module scripts
 */
function checkForModuleSupport() {
  if ('supports' in HTMLScriptElement) {
    return HTMLScriptElement.supports('module');
  }
  return false;
}

/*
Loads billdesk scripts and styles.
Returns loading and error states.

Uses the following sources from config:
- BILLDESK_SOURCES.module: ES module script
- BILLDESK_SOURCES.noModule: nomodule script fallback
- BILLDESK_SOURCES.css: styles
*/
export const useBillDesk = (enabled: boolean) => {
  const initialized = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (initialized.current || !enabled) return;
    setLoading(true);
    const sources = {
      module: config.BILLDESK_SOURCES.module,
      noModule: config.BILLDESK_SOURCES.noModule,
      css: config.BILLDESK_SOURCES.css
    };

    // Load styles for billdesk
    const style = document.createElement('link');
    style.rel = 'stylesheet';
    style.href = sources.css;
    style.onerror = () => {
      setError(true);
      setLoading(false);
      captureException(new URIError(`Failed to load billdesk css: ${sources.css}.`));
    };
    document.head.appendChild(style);

    if (checkForModuleSupport()) {
      // Load ES module script
      const moduleScript = document.createElement('script');
      moduleScript.src = sources.module;
      moduleScript.type = 'module';
      moduleScript.async = true;
      moduleScript.onload = () => {
        initialized.current = true;
        setLoading(false);
      };
      moduleScript.onerror = () => {
        setError(true);
        setLoading(false);
        captureException(
          new URIError(`Failed to load billdesk module script: ${sources.module}.`)
        );
      };
      document.head.appendChild(moduleScript);
    } else {
      // Load nomodule script fallback
      const noModuleScript = document.createElement('script');
      noModuleScript.src = sources.noModule;
      noModuleScript.noModule = true;
      noModuleScript.async = true;
      noModuleScript.onload = () => {
        initialized.current = true;
        setLoading(false);
      };
      noModuleScript.onerror = () => {
        setError(true);
        setLoading(false);
        captureException(
          new URIError(
            `Failed to load billdesk nomodule script: ${sources.noModule}.`
          )
        );
      };
      document.head.appendChild(noModuleScript);
    }
  }, [enabled]);

  return { error, loading };
};

export const usePolicyId = () => {
  const { policyId, policyID } = useParams();
  return policyId || policyID;
};

/**
 * Logout of the account if already logged in
 */
export const useAccountLogout = () => {
  const { loggedIn, logout } = useAccount();

  useEffect(() => {
    if (loggedIn) {
      logout();
    }
  }, [loggedIn]);

  return null;
};

/**
 * Prevent page unload
 * @param shouldPrevent - Prevent page unload if true
 * @returns A callback function to disable the prevention
 */
export const usePreventPageUnload = (shouldPrevent: boolean) => {
  const handlerRef = useRef<((event: BeforeUnloadEvent) => void) | null>(null);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (shouldPrevent) {
        event.preventDefault();
        event.returnValue = ''; // Required for Chrome to trigger the alert
      }
    };

    handlerRef.current = handleBeforeUnload;

    if (shouldPrevent) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldPrevent]);

  const disablePreventUnload = () => {
    if (handlerRef.current) {
      window.removeEventListener('beforeunload', handlerRef.current);
      handlerRef.current = null;
    }
  };

  return disablePreventUnload;
};

export const useGetLoginTypeAndInternalId = (): LoginTypeInfo => {
  const location = useLocation();
  const params = useParams<{ policyId?: string; policyID?: string }>();

  const [loginTypeData, setLoginTypeData] = useState<LoginTypeInfo>({
    type: LoginType.GENERAL,
    internal_id: null
  });

  useEffect(() => {
    const pathname = location.pathname;

    // Extract the internal ID (policyId or policyID from URL params)
    const internalId = params?.policyId || params?.policyID || null;

    // Determine type based on URL patterns
    if (pathname.includes('/account') || pathname.includes('/login')) {
      setLoginTypeData({
        type: LoginType.DITTO_ACCOUNT,
        internal_id: null
      });
      return;
    }

    // Check for health-related routes
    if (pathname.includes('/health')) {
      // General health routes without specific policy ID
      if (
        pathname.includes('/health/quote') ||
        pathname.includes('/health/fq') ||
        pathname.includes('/health/optima-secure-migration')
      ) {
        setLoginTypeData({
          type: LoginType.GENERAL,
          internal_id: null
        });
        return;
      }

      // Health routes with policy ID
      setLoginTypeData({
        type: LoginType.HEALTH,
        internal_id: internalId
      });
      return;
    }

    // Check for term-related routes
    if (pathname.includes('/term')) {
      // General term routes without specific policy ID
      if (pathname.includes('/term/quote') || pathname.includes('/term/fq')) {
        setLoginTypeData({
          type: LoginType.GENERAL,
          internal_id: null
        });
        return;
      }

      // Term routes with policy ID
      setLoginTypeData({
        type: LoginType.TERM,
        internal_id: internalId
      });
      return;
    }

    // Default case for other routes
    setLoginTypeData({
      type: LoginType.GENERAL,
      internal_id: null
    });
  }, [location.pathname, params?.policyId, params?.policyID]);

  return loginTypeData;
};
